import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage, getStorageItem, STORAGE_KEYS } from './storage'
import { fileTokenService } from '@/lib/services/fileTokenService'
import { showError } from '@/lib/utils/notifications'

/**
 * File types for different document categories
 */
export type FileType =
  | 'ID_PROOF'
  | 'PROOF_OF_INCOME'
  | 'ADDITIONAL_DOCUMENTS'
  | 'GOVERNMENT_ID'
  | 'GOVERNMENT_ID_BACK'
  | 'SIGNATURE'
  | 'BANK_STATEMENT'
  | string

/**
 * Module types for file access
 */
export type ModuleType = 'APPLICATION' | 'NOTES' | 'CHARGEBACK' | 'CONFIGURATION' | string

/**
 * Get file extension from filename or path
 */
export function getFileExtension(fileName: string): string {
  if (!fileName) return ''
  // Remove query params if any (match Angular implementation)
  const clean = fileName.split('?')[0]
  return clean.includes('.') ? clean.split('.').pop()!.toLowerCase() : ''
}

/**
 * Check if file is HEIC or HEIF — browsers can't display these in <img>;
 * we convert to JPEG client-side for preview.
 */
export function isHeicOrHeifFile(fileName: string): boolean {
  const ext = getFileExtension(fileName)
  return ext === 'heic' || ext === 'heif'
}

/**
 * Check if file is an image based on extension
 */
export function isImageFile(fileName: string): boolean {
  const extension = getFileExtension(fileName)
  const imageExtensions = ['jpeg', 'jpg', 'gif', 'png', 'webp', 'bmp', 'svg', 'heic', 'heif']
  return imageExtensions.includes(extension)
}

/**
 * Check if file is a document (non-image) based on extension
 */
export function isDocumentFile(fileName: string): boolean {
  const extension = getFileExtension(fileName)
  const documentExtensions = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx', 'txt']
  return documentExtensions.includes(extension)
}

/** Angular `downloadFilePreview` non-image extensions — open directly, not in file-handlers. */
const NON_IMAGE_PREVIEW_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx', 'txt'] as const

function isNonImagePreviewDocument(fileName: string): boolean {
  const extension = getFileExtension(fileName)
  return NON_IMAGE_PREVIEW_EXTENSIONS.includes(
    extension as (typeof NON_IMAGE_PREVIEW_EXTENSIONS)[number],
  )
}

/**
 * Check if URL is an image serving endpoint
 */
export function isImageServingUrl(url: string): boolean {
  return (
    url.includes('/serve-file/') ||
    url.includes('/serve-file-claims/') ||
    url.includes('/serve-file-preview/') ||
    url.includes('/serve-file-claims-preview/') ||
    url.includes('/stream-file')
  )
}

/**
 * Ensure file access token is valid (refresh if needed). Call before generating preview URLs
 * to avoid "token expired" when opening preview or download.
 */
export async function ensureFileToken(): Promise<string | null> {
  return fileTokenService.ensureFileToken()
}

/**
 * Build a `/stream-file` URL for path-based previews (dev: same-origin via Vite proxy).
 */
export function buildStreamFileUrl(filePath: string, disk = 's3'): string {
  const base = getBrowserApiBaseUrl()
  return `${base}/stream-file?path=${encodeURIComponent(filePath)}&disk=${disk}`
}

/**
 * Generate secure file preview URL (uses token from storage; call ensureFileToken() first).
 * For opening preview with a guaranteed fresh token, use generateFilePreviewUrlWithToken.
 */
export function generateFilePreviewUrl(
  fileId: string | number,
  fileType: FileType,
  refId: string | number,
  module: ModuleType = 'APPLICATION'
): string {
  const fileUrl = `${getBrowserApiBaseUrl()}/serve-file-preview/${fileId}`
  const fileAccessToken = fileTokenService.getFileAccessToken() || getStorageItem(STORAGE_KEYS.fileAccessToken) || ''

  const params = new URLSearchParams({
    fileType: fileType.toString(),
    RefId: refId.toString(),
    module: module.toString(),
    access_token: fileAccessToken,
  })

  return `${fileUrl}?${params.toString()}`
}

/**
 * Generate secure file preview URL with an explicit token (e.g. from ensureFileToken()).
 * Use this when opening preview so the URL always has a valid token and never reuses a stale one.
 */
export function generateFilePreviewUrlWithToken(
  fileId: string | number,
  fileType: FileType,
  refId: string | number,
  module: ModuleType,
  accessToken: string
): string {
  const fileUrl = `${getBrowserApiBaseUrl()}/serve-file-preview/${fileId}`
  const params = new URLSearchParams({
    fileType: fileType.toString(),
    RefId: refId.toString(),
    module: module.toString(),
    access_token: accessToken,
  })
  return `${fileUrl}?${params.toString()}`
}

/**
 * Generate secure file download URL
 * @param fileId - File attachment ID
 * @param fileType - Type of file
 * @param refId - Reference ID
 * @param module - Module name
 * @returns Secure file download URL
 */
export function generateFileDownloadUrl(
  fileId: string | number,
  fileType: FileType,
  refId: string | number,
  module: ModuleType = 'APPLICATION'
): string {
  return `${getBrowserApiBaseUrl()}/serve-file/${module}/${refId}/${fileType}/${fileId}`
}

/**
 * Generate configuration file URL
 * @param fileType - Type of file
 * @param fileId - File ID
 * @param encodedPath - Encoded S3 key or file path
 * @returns Configuration file URL
 */
export function generateConfigFileUrl(
  fileType: FileType,
  fileId: string | number,
  encodedPath: string
): string {
  return `${getBrowserApiBaseUrl()}/serve-file-config/${fileType}/${fileId}/?url=${encodedPath}`
}

/**
 * Generate claims file URL
 * @param fileType - Type of file
 * @param fileId - File ID
 * @param encodedPath - Encoded S3 key or file path
 * @returns Claims file URL
 */
export function generateClaimsFileUrl(
  fileType: FileType,
  fileId: string | number,
  encodedPath: string
): string {
  return `${getBrowserApiBaseUrl()}/serve-file-claims/${fileType}/${fileId}/?url=${encodedPath}`
}

/**
 * Generate claims file preview URL
 * @param fileId - File ID
 * @param fileType - Type of file
 * @param encodedPath - Encoded S3 key or file path
 * @returns Claims file preview URL with access token
 */
export function generateClaimsFilePreviewUrl(
  fileId: string | number,
  fileType: FileType,
  encodedPath: string
): string {
  const fileUrl = `${getBrowserApiBaseUrl()}/serve-file-claims-preview/${fileId}`
  const fileAccessToken = fileTokenService.getFileAccessToken() || getStorageItem(STORAGE_KEYS.fileAccessToken) || ''

  const params = new URLSearchParams({
    fileType: fileType.toString(),
    url: encodedPath,
    access_token: fileAccessToken,
  })

  return `${fileUrl}?${params.toString()}`
}

/**
 * Open file preview in new window.
 * Ensures a fresh file access token before opening to prevent "token expired" errors.
 * Opens through the local file-handlers route so preview tabs keep the app title/icon.
 * @param fileId - File attachment ID
 * @param fileType - Type of file
 * @param fileName - File name (for determining if image or document)
 * @param refId - Reference ID
 * @param module - Module name
 * @param filesList - Optional array of file URLs for navigation (must be built after ensureFileToken() when using token URLs)
 */
/** Credit/report downloads use session JWT (`authService.getToken()` in Angular), not `file_access_token`. */
function usesSessionJwtAccessToken(path: string): boolean {
  return (
    /\/api\/report\//i.test(path) ||
    /\/api\/credit_report\//i.test(path) ||
    /\/api\/download_/i.test(path) ||
    /\/api\/export\//i.test(path)
  )
}

/** serve-file-preview URLs use the dedicated file access token from `/refresh-file-token`. */
function usesFileAccessToken(path: string): boolean {
  return (
    path.includes('/serve-file-preview/') ||
    path.includes('/serve-file-claims-preview/')
  )
}

/**
 * Replace or add access_token on a file/report URL (fresh token before preview open).
 */
export function applyFreshAccessTokenToUrl(filePath: string, token: string): string {
  if (!token.trim()) return filePath
  try {
    const u = new URL(filePath)
    u.searchParams.set('access_token', token)
    return u.toString()
  } catch {
    if (/[?&]access_token=/.test(filePath)) {
      return filePath.replace(
        /([?&])access_token=[^&]*/,
        `$1access_token=${encodeURIComponent(token)}`,
      )
    }
    const joiner = filePath.includes('?') ? '&' : '?'
    return `${filePath}${joiner}access_token=${encodeURIComponent(token)}`
  }
}

function replaceTokenInPreviewUrl(url: string, freshToken: string): string {
  if (!url.includes('/serve-file-preview/')) return url
  return applyFreshAccessTokenToUrl(url, freshToken)
}

export async function openFilePreview(
  fileId: string | number,
  fileType: FileType,
  fileName: string,
  refId: string | number,
  module: ModuleType = 'APPLICATION',
  filesList?: string[]
): Promise<void> {
  const token = await fileTokenService.ensureFileToken()
  if (!token || token.trim() === '') {
    showError('Unable to open file preview. Please try again in a moment or refresh the page and click the file again.')
    return
  }
  const secureUrl = generateFilePreviewUrlWithToken(fileId, fileType, refId, module, token)

  // Angular downloadFilePreview: PDFs and other documents open directly in a new tab.
  if (isNonImagePreviewDocument(fileName)) {
    window.open(secureUrl, '_blank', 'noopener,noreferrer')
    return
  }

  // Ensure all navigation URLs use the same fresh token (avoids "token does not exist" when switching files)
  const listWithFreshToken =
    filesList && filesList.length > 0
      ? filesList.map((url) => replaceTokenInPreviewUrl(url, token))
      : undefined

  let fileHandlersUrl = `/file-handlers?path=${encodeURIComponent(secureUrl)}`
  if (fileName) {
    fileHandlersUrl += `&name=${encodeURIComponent(fileName)}`
  }
  if (listWithFreshToken && listWithFreshToken.length > 0) {
    fileHandlersUrl += `&filesList=${encodeURIComponent(JSON.stringify(listWithFreshToken))}`
  }
  window.open(fileHandlersUrl, '_blank', 'noopener,noreferrer')
}

/**
 * Open a preview URL with Angular parity:
 * - serve-file-preview documents (PDF, etc.) → direct new tab
 * - images and stream-file paths → /file-handlers viewer
 */
export function openPreviewTarget(
  url: string,
  fileName?: string,
  imageFilesList?: string[],
): void {
  if (url.includes('/serve-file-preview/') && isNonImagePreviewDocument(fileName || url)) {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }

  openFilePreviewByPath(url, fileName, imageFilesList)
}

/**
 * Open file preview using file path (for stream-file URLs, credit report URLs, etc.)
 * Angular opens stored paths as-is; we only refresh the token type each endpoint expects.
 */
export function openFilePreviewByPath(filePath: string, fileName?: string, filesList?: string[]): void {
  void (async () => {
    let path = filePath
    let list = filesList

    if (usesSessionJwtAccessToken(path)) {
      const sessionToken = getAuthTokenFromStorage()
      if (sessionToken) {
        path = applyFreshAccessTokenToUrl(path, sessionToken)
        if (list?.length) {
          list = list.map((url) =>
            usesSessionJwtAccessToken(url)
              ? applyFreshAccessTokenToUrl(url, sessionToken)
              : url,
          )
        }
      }
      // Angular file-handlers navigates PDF / access_token report URLs directly (not iframe).
      const directUrl = `${path}${path.includes('?') ? '&' : '?'}time=${Date.now()}`
      window.open(directUrl, '_blank', 'noopener,noreferrer')
      return
    } else if (usesFileAccessToken(path)) {
      const fileToken = await ensureFileToken()
      if (fileToken) {
        path = applyFreshAccessTokenToUrl(path, fileToken)
        if (list?.length) {
          list = list.map((url) =>
            usesFileAccessToken(url) ? applyFreshAccessTokenToUrl(url, fileToken) : url,
          )
        }
      }
    }

    let fileHandlersUrl = `/file-handlers?path=${encodeURIComponent(path)}`
    if (fileName) {
      fileHandlersUrl += `&name=${encodeURIComponent(fileName)}`
    }
    if (list && list.length > 0) {
      fileHandlersUrl += `&filesList=${encodeURIComponent(JSON.stringify(list))}`
    }
    window.open(fileHandlersUrl, '_blank', 'noopener,noreferrer')
  })()
}

